import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { Avatar, Drawer, Loading } from '@ibk/shared-ui';
import { formatCurrency } from '@ibk/shared-utils';
import { useGetConsentTransactionsQuery } from '../../../../services/openFinanceApi';
import accountsStyle from '../../ContabilidadeDigitalDasboard.module.css';
import styles from './AccountTransactions.module.css';
import { Filters } from './components/Filters';
import { TransactionsList } from './components/TransactionsList';
import { getDefaultDateRange, manageTransactionsList } from './helpers';
const EMPTY_TRANSACTIONS = [];
const DEFAULT_PAGE_SIZE = 10;
const SCROLL_THRESHOLD = 100;
const defaultValues = {
    direction: undefined,
    type: undefined,
    from: getDefaultDateRange().from,
    to: getDefaultDateRange().to,
};
export const AccountTransactions = ({ isOpen, onClose, accountName, accountHolder, accountNumber, bankLogo, accountId, balance, }) => {
    const { t } = useTranslation();
    const [filters, setFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [accumulatedTransactions, setAccumulatedTransactions] = useState([]);
    const scrollContainerRef = useRef(null);
    useEffect(() => {
        const { from: defaultFrom, to: defaultTo } = getDefaultDateRange();
        setFilters({ from: defaultFrom, to: defaultTo });
    }, []);
    const queryParams = useMemo(() => {
        const { from: defaultFrom, to: defaultTo } = getDefaultDateRange();
        return {
            accountId,
            pageSize: DEFAULT_PAGE_SIZE,
            page: currentPage,
            ...(filters.from ? { from: filters.from } : { from: defaultFrom }),
            ...(filters.to ? { to: filters.to } : { to: defaultTo }),
            ...(filters.direction ? { tipe: filters.direction === 'credit' ? 'CREDIT' : 'DEBIT' } : {}),
        };
    }, [accountId, filters, currentPage]);
    const shouldSkipQuery = !isOpen || !accountId;
    const { data, isLoading, isFetching } = useGetConsentTransactionsQuery({ accountId, query: queryParams }, { skip: shouldSkipQuery });
    const totalPages = data?.result?.totalPages ?? 1;
    const responsePage = data?.result?.page;
    const hasMorePages = currentPage < totalPages;
    const newTransactions = data?.result?.results ?? EMPTY_TRANSACTIONS;
    useEffect(() => {
        if (newTransactions.length > 0 && responsePage === currentPage) {
            setAccumulatedTransactions(prev => {
                if (currentPage === 1) {
                    return newTransactions;
                }
                const existingIds = new Set(prev.map(t => t.id));
                const uniqueNewTransactions = newTransactions.filter(t => !existingIds.has(t.id));
                return [...prev, ...uniqueNewTransactions];
            });
        }
    }, [newTransactions, currentPage, responsePage]);
    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container || isFetching || !hasMorePages) {
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = container;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;
        if (isNearBottom) {
            setCurrentPage(prev => prev + 1);
        }
    }, [isFetching, hasMorePages]);
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);
    const transactions = useMemo(() => {
        if (currentPage === 1 && accumulatedTransactions.length === 0) {
            return newTransactions;
        }
        return accumulatedTransactions;
    }, [currentPage, accumulatedTransactions, newTransactions]);
    useEffect(() => {
        setCurrentPage(1);
        setAccumulatedTransactions([]);
    }, [accountId]);
    const resolvedAccountName = accountName;
    const resolvedLogo = bankLogo;
    const resolvedBalance = balance;
    const groupedTransactions = useMemo(() => {
        return manageTransactionsList(transactions, filters, t);
    }, [transactions, filters, t]);
    useEffect(() => {
        if (!isFetching && groupedTransactions.total < DEFAULT_PAGE_SIZE && hasMorePages) {
            setCurrentPage(prev => prev + 1);
        }
    }, [groupedTransactions, hasMorePages, isFetching]);
    const handleApplyFilters = useCallback((newFilters) => {
        setAccumulatedTransactions([]);
        setCurrentPage(1);
        setFilters(newFilters);
    }, []);
    const shouldShowEmptyState = groupedTransactions.transactions.length === 0 && !(isLoading || isFetching);
    return (_jsx(Drawer, { isOpen: isOpen, onClose: onClose, "data-testid": 'drawerAccountTransactions', showCloseButton: false, onReturn: onClose, title: t('contabilidade-digital.account-transactions.title'), customStyles: {
            'drawer-header-title': styles.headerTitle,
            'drawer-body': styles.drawerBody,
            'drawer-header-left': styles.headerLeft,
        }, children: _jsxs("div", { className: styles.container, "data-testid": 'containerAccountTransactions', children: [_jsx(Filters, { onApplyFilters: handleApplyFilters, defaultValues: defaultValues }), _jsxs("div", { className: styles.accountCard, "data-testid": 'containerAccountTransactionsAccountCard', children: [_jsxs("div", { className: styles.accountHeader, children: [resolvedLogo ? (_jsx(Avatar, { src: resolvedLogo, alt: resolvedAccountName, type: 'image', className: `${styles.accountLogo} ${accountsStyle.avatar}`, "data-testid": 'avatarAccountTransactionsLogo' })) : (_jsx(Avatar, { alt: resolvedAccountName, type: 'inicial', className: `${styles.accountLogoFallback} ${accountsStyle.avatar}`, "data-testid": 'avatarAccountTransactionsInitial' })), _jsxs("div", { className: styles.accountInfo, children: [_jsx("p", { className: styles.accountBank, "data-testid": 'textViewAccountTransactionsAccountName', children: resolvedAccountName }), accountHolder && (_jsx("p", { className: styles.accountHolder, "data-testid": 'textViewAccountTransactionsAccountHolder', children: accountHolder })), accountNumber && (_jsx("p", { className: styles.accountNumber, "data-testid": 'textViewAccountTransactionsAccountNumber', children: t('contabilidade-digital.account-transactions.account-number', { number: accountNumber }) }))] })] }), _jsxs("div", { className: styles.balanceSection, "data-testid": 'containerAccountTransactionsBalance', children: [_jsx("p", { className: styles.balanceLabel, "data-testid": 'textViewAccountTransactionsBalanceLabel', children: t('contabilidade-digital.account-transactions.balance-label') }), _jsx("p", { className: styles.balanceValue, "data-testid": 'textViewAccountTransactionsBalanceValue', children: formatCurrency(resolvedBalance ?? 0) })] })] }), _jsxs("div", { ref: scrollContainerRef, className: styles.groups, "data-testid": 'listAccountTransactions', children: [_jsx(TransactionsList, { isLoading: isLoading, isFetching: isFetching, currentPage: currentPage, shouldShowEmptyState: shouldShowEmptyState, groupedTransactions: groupedTransactions.transactions }), isFetching && currentPage > 1 && (_jsx("div", { className: styles.loadingMore, "data-testid": 'containerAccountTransactionsLoadingMore', children: _jsx(Loading, { size: 'small' }) }))] })] }) }));
};
export default AccountTransactions;
